/**
 * BoxLabelsTool - Standalone box label generator.
 * SOP-style PIN gate → enter a PrintLogic job number → preview → pick boxes → download PDF.
 * Route: /box-labels
 */
import { useState } from "react";
import axios from "axios";
import {
  Tags, Lock, Loader2, AlertTriangle, Search, Download, Home, Package
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoxLabelsTool = () => {
  const [pin, setPin] = useState("");
  const [verified, setVerified] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [checking, setChecking] = useState(false);

  const [jobNumber, setJobNumber] = useState("");
  const [preview, setPreview] = useState(null); // { job_number }
  const [fields, setFields] = useState(null); // editable label data
  const [boxes, setBoxes] = useState(4);
  const [numbering, setNumbering] = useState(true);
  const [labelFormat, setLabelFormat] = useState("a4");
  const [looking, setLooking] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  const setField = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));

  const lookup = async () => {
    const job = jobNumber.trim();
    if (!job) return;
    setLooking(true);
    setError("");
    setPreview(null);
    setFields(null);
    setNotFound(false);
    try {
      const res = await axios.get(`${API}/sop/jobs/${encodeURIComponent(job)}/label-preview?pin=${encodeURIComponent(pin)}`);
      setPreview({ job_number: res.data.job_number });
      setFields({
        customer: res.data.customer || "",
        details: res.data.details || "",
        quantity: res.data.quantity || "",
        per_carton: "",
        po_number: res.data.po_number || "",
        date_required: res.data.date_required || "",
        rep_name: res.data.rep_name || ""
      });
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
        setError("Job not found in PrintLogic — check the number, or type the details manually below.");
      } else {
        setError(err.response?.data?.message || "Lookup failed — try again.");
      }
    } finally {
      setLooking(false);
    }
  };

  const startManual = () => {
    const job = jobNumber.trim().replace(/[^0-9A-Za-z-]/g, "") || "LABEL";
    setPreview({ job_number: job });
    setFields({ customer: "", details: "", quantity: "", per_carton: "", po_number: "", date_required: "", rep_name: "" });
    setNotFound(false);
    setError("");
  };

  const downloadHref = preview && fields
    ? `${API}/sop/jobs/${encodeURIComponent(preview.job_number)}/box-labels?pin=${encodeURIComponent(pin)}&boxes=${boxes || 1}&numbering=${numbering}&label_format=${labelFormat}` +
      Object.entries(fields).map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join("")
    : "#";

  const verifyPin = async () => {
    setChecking(true);
    setPinError(false);
    try {
      const res = await axios.get(`${API}/sop/verify-access?pin=${encodeURIComponent(pin)}`);
      if (res.data.valid) setVerified(true);
      else setPinError(true);
    } catch {
      setPinError(true);
    } finally {
      setChecking(false);
    }
  };

  if (!verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 to-slate-950 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md" data-testid="labels-pin-screen">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Tags className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Box Labels</h1>
            <p className="text-gray-500 mt-2">Enter PIN to continue</p>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && verifyPin()}
              placeholder="Enter PIN"
              autoFocus
              data-testid="labels-pin-input"
              className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-lg text-gray-900 focus:outline-none transition-colors ${pinError ? "border-red-500 bg-red-50" : "border-gray-200 focus:border-emerald-500"}`}
            />
          </div>
          {pinError && (
            <div className="flex items-center gap-2 text-red-600 text-sm mt-3" data-testid="labels-pin-error">
              <AlertTriangle className="w-4 h-4" /> Invalid PIN. Please try again.
            </div>
          )}
          <button
            onClick={verifyPin}
            disabled={checking || !pin}
            data-testid="labels-pin-submit"
            className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center"
          >
            {checking ? <Loader2 className="w-5 h-5 animate-spin" /> : "Unlock"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="labels-tool">
      <header className="bg-white border-b border-gray-300 shadow-sm px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Tags className="w-7 h-7 text-emerald-600" />
            <h1 className="text-xl font-black text-gray-800 tracking-tight">BOX <span className="text-emerald-600">LABELS</span></h1>
          </div>
          <a href="/hub" className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg" title="Back to Hub" data-testid="labels-hub-link">
            <Home className="w-5 h-5 text-gray-600" />
          </a>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Job lookup */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">PrintLogic Job Number</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={jobNumber}
              onChange={(e) => { setJobNumber(e.target.value); setPreview(null); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="e.g. 68245"
              autoFocus
              data-testid="labels-job-input"
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-lg text-gray-900"
            />
            <button
              onClick={lookup}
              disabled={looking || !jobNumber.trim()}
              data-testid="labels-lookup-btn"
              className="px-5 py-3 sm:py-0 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-bold flex items-center justify-center gap-2"
            >
              {looking ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} Find Job
            </button>
          </div>
          {error && (
            <p className="flex items-center gap-2 text-red-600 text-sm mt-3" data-testid="labels-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
            </p>
          )}
          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-gray-400">No job number? Create a label from scratch.</p>
            <button
              onClick={startManual}
              data-testid="labels-manual-btn"
              className="px-4 py-2 rounded-xl bg-gray-700 hover:bg-gray-800 text-white text-sm font-semibold"
            >
              Type details manually
            </button>
          </div>
        </div>

        {/* Editable preview + download */}
        {preview && fields && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-testid="labels-preview">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                <h2 className="text-lg font-bold text-gray-800">Job #{preview.job_number}</h2>
              </div>
              <span className="text-xs text-gray-400">Edit any box — the labels use what you type</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-5">
              <div className="sm:col-span-2">
                <label className="text-gray-400 text-xs uppercase tracking-wide">Customer</label>
                <input value={fields.customer} onChange={setField("customer")} data-testid="labels-field-customer"
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none font-semibold text-gray-800" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-gray-400 text-xs uppercase tracking-wide">Details</label>
                <textarea value={fields.details} onChange={setField("details")} rows={2} data-testid="labels-field-details"
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none resize-none text-gray-700" />
              </div>
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wide">Total Qty</label>
                <input value={fields.quantity} onChange={setField("quantity")} data-testid="labels-field-quantity"
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-gray-700" />
              </div>
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wide">Total Per Carton</label>
                <input value={fields.per_carton} onChange={setField("per_carton")} data-testid="labels-field-percarton"
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-gray-700" />
              </div>
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wide">PO Number</label>
                <input value={fields.po_number} onChange={setField("po_number")} data-testid="labels-field-po"
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-gray-700" />
              </div>
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wide">Date Required</label>
                <input value={fields.date_required} onChange={setField("date_required")} data-testid="labels-field-date"
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-gray-700" />
              </div>
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wide">Rep</label>
                <input value={fields.rep_name} onChange={setField("rep_name")} data-testid="labels-field-rep"
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none text-gray-700" />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Label size</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setLabelFormat("a4")}
                  data-testid="labels-format-a4"
                  className={`py-2.5 px-3 rounded-xl font-semibold text-sm border-2 text-left ${labelFormat === "a4" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-gray-200 text-gray-500"}`}
                >
                  A4 sheet <span className="font-normal">— 4 labels per page (office printer)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setLabelFormat("zebra")}
                  data-testid="labels-format-zebra"
                  className={`py-2.5 px-3 rounded-xl font-semibold text-sm border-2 text-left ${labelFormat === "zebra" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-gray-200 text-gray-500"}`}
                >
                  Zebra roll <span className="font-normal">— 100 × 73mm, 1 per label</span>
                </button>
              </div>
              {labelFormat === "zebra" && (
                <p className="text-xs text-gray-400 mt-2">Print at 100% / "Actual size" with the ZDesigner driver stock set to 100 × 73mm.</p>
              )}
            </div>

            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of boxes</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={boxes}
                  onChange={(e) => setBoxes(e.target.value.replace(/[^0-9]/g, ""))}
                  data-testid="labels-boxes-input"
                  className="w-28 px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-gray-900"
                />
              </div>
              <label className="flex items-center gap-2 pb-3 cursor-pointer select-none" data-testid="labels-numbering-toggle">
                <input
                  type="checkbox"
                  checked={numbering}
                  onChange={(e) => setNumbering(e.target.checked)}
                  data-testid="labels-numbering-checkbox"
                  className="w-4 h-4 accent-emerald-600"
                />
                <span className="text-sm text-gray-700">Number cartons ("1 of {boxes || 1}")</span>
              </label>
              <a
                href={downloadHref}
                data-testid="labels-download-btn"
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" /> Download Labels PDF
              </a>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              {labelFormat === "zebra"
                ? `100 × 73mm PDF — one label per page (${boxes || 1} page${(boxes || 1) > 1 ? "s" : ""})${numbering ? ", numbered" : ""} with QR code`
                : `A4 PDF — 4 × A6 labels per sheet${numbering ? `, numbered "Box 1 of ${boxes || 1}"` : ""} with QR code`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoxLabelsTool;
